import {Fragment, ReactElement, useContext, useState} from 'react';
import {ListboxItemProps} from '@ui/forms/listbox/item';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {useLogout} from '@common/auth/requests/logout';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {useSettings} from '@ui/settings/use-settings';
import {useAuth} from '@common/auth/use-auth';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {useThemeSelector} from '@ui/themes/theme-selector-context';
import {Menu, MenuItem, MenuTrigger} from '@ui/menu/menu-trigger';
import {SettingsIcon} from '@ui/icons/material/Settings';
import {Trans} from '@ui/i18n/trans';
import {PaymentsIcon} from '@ui/icons/material/Payments';
import {createSvgIconFromTree} from '@ui/icons/create-svg-icon';
import {AccountCircleIcon} from '@ui/icons/material/AccountCircle';
import {DarkModeIcon} from '@ui/icons/material/DarkMode';
import {LightModeIcon} from '@ui/icons/material/LightMode';
import {ExitToAppIcon} from '@ui/icons/material/ExitToApp';
import {useUserRolesAccess} from '@common/auth/user-roles-access';
import {QuestionMarkIcon} from '@ui/icons/material/QuestionMark';
import {SupportAgentIcon} from '@ui/icons/material/SupportAgent';
import {FactCheckIcon} from '@ui/icons/material/FactCheck';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {DevicesIcon} from '@ui/icons/material/Devices';
import {PhoneIphoneIcon} from '@ui/icons/material/PhoneIphone';
import {AndroidIcon} from '@ui/icons/material/Android';
import {CheckIcon} from '@ui/icons/material/Check';
import {
  AppPlatformMode,
  getAppPlatformMode,
  isMobileApp,
  setAppPlatformMode,
} from '@common/core/platform-detection';

interface Props {
  children: ReactElement;
  items?: ReactElement<ListboxItemProps>[];
}

export function NavbarAuthMenu({children, items}: Props) {
  const {auth} = useContext(SiteConfigContext);
  const logout = useLogout();
  const menu = useCustomMenu('auth-dropdown');
  const {themes} = useSettings();
  const {user, isSubscribed} = useAuth();
  const navigate = useNavigate();
  const {selectedTheme, selectTheme} = useThemeSelector();
  const {isAdmin} = useUserRolesAccess();
  const [isSwitchingPlatform, setIsSwitchingPlatform] = useState(false);

  if (!selectedTheme || !user) return null;

  const supportUrl = getBootstrapData().settings.support_url;
  const isMenuItems = menu?.items.length;
  const canSwitchAppPlatform = isAdmin && isMobileApp();
  const canUserChangeTheme = !!themes?.user_change;
  const appPlatformMode = getAppPlatformMode();
  
  const handlePlatformModeSelection = async (mode: AppPlatformMode) => {
    if (isSwitchingPlatform || mode === appPlatformMode) {
      return;
    }

    setIsSwitchingPlatform(true);
    await setAppPlatformMode(mode);
    window.location.reload();
  };

  const billingMenuItem = (
    <MenuItem
      value="billing"
      startIcon={<PaymentsIcon />}
      onSelected={() => {
        navigate('/billing');
      }}
    >
      <Trans message="Billing" />
    </MenuItem>
  );

  return (
    <MenuTrigger>
      {children}
      <Menu>
        {menu &&
          menu.items.map(item => {
            const Icon = item.icon && createSvgIconFromTree(item.icon);
            return (
              <MenuItem
                value={item.id}
                key={item.id}
                startIcon={Icon && <Icon />}
                onSelected={() => {
                  if (item.type === 'link') {
                    window.open(item.action, '_blank');
                  } else {
                    navigate(item.action);
                  }
                }}
              >
                <Trans message={item.label} />
              </MenuItem>
            );
          })}

        {isMenuItems && <MenuItem value="sep1" isSeparator />}

        {auth.getUserProfileLink && (
          <MenuItem
            value="profile"
            startIcon={<AccountCircleIcon />}
            onSelected={() => {
              navigate(auth.getUserProfileLink!(user));
            }}
          >
            <Trans message="Profile page" />
          </MenuItem>
        )}
        {auth.getUserProfileLink && (
          <MenuItem
            value="settings"
            startIcon={<SettingsIcon />}
            onSelected={() => {
              navigate('/user/account-settings');
            }}
          >
            <Trans message="Account settings" />
          </MenuItem>
        )}
        {isSubscribed && billingMenuItem}

        {items?.length && <MenuItem value="sepItems" isSeparator />}
        {items?.map(item => item)}

        <MenuItem value="sep2" isSeparator />

        {canUserChangeTheme && !selectedTheme.is_dark && (
          <MenuItem
            value="light"
            startIcon={<DarkModeIcon />}
            onSelected={() => {
              selectTheme('dark');
            }}
          >
            <Trans message="Dark mode" />
          </MenuItem>
        )}
        {canUserChangeTheme && selectedTheme.is_dark && (
          <MenuItem
            value="dark"
            startIcon={<LightModeIcon />}
            onSelected={() => {
              selectTheme('light');
            }}
          >
            <Trans message="Light mode" />
          </MenuItem>
        )}

        {canSwitchAppPlatform && (
          <MenuItem
            value="platform-auto"
            startIcon={<DevicesIcon />}
            endIcon={appPlatformMode === 'auto' ? <CheckIcon /> : undefined}
            isDisabled={isSwitchingPlatform}
            onSelected={() => {
              void handlePlatformModeSelection('auto');
            }}
          >
            <Trans message="Auto" />
          </MenuItem>
        )}
        
        {canSwitchAppPlatform && (
          <MenuItem
            value="platform-ios"
            startIcon={<PhoneIphoneIcon />}
            endIcon={appPlatformMode === 'ios' ? <CheckIcon /> : undefined}
            isDisabled={isSwitchingPlatform}
            onSelected={() => {
              void handlePlatformModeSelection('ios');
            }}
          >
            <Trans message="iOS" />
          </MenuItem>
        )}
        
        {canSwitchAppPlatform && (
          <MenuItem
            value="platform-android"
            startIcon={<AndroidIcon />}
            endIcon={
              appPlatformMode === 'android' ? <CheckIcon /> : undefined
            }
            isDisabled={isSwitchingPlatform}
            onSelected={() => {
              void handlePlatformModeSelection('android');
            }}
          >
            <Trans message="Android" />
          </MenuItem>
        )}
        
        {canSwitchAppPlatform && (
          <MenuItem value="sep2.5" isSeparator />
        )}

        <MenuItem
          value="support"
          startIcon={<SupportAgentIcon />}
          onSelected={() => {
            window.open(supportUrl, '_blank');
          }}
        >
          <Trans message="Support" />
        </MenuItem>
        <MenuItem
          value="about-us"
          startIcon={<QuestionMarkIcon />}
          onSelected={() => {
            navigate('/in/pages/about-us');
          }}
        >
          <Trans message="About Us" />
        </MenuItem>
        <MenuItem
          value="legal"
          startIcon={<FactCheckIcon />}
          onSelected={() => {
            navigate('/in/pages/legal');
          }}
        >
          <Trans message="Legals" />
        </MenuItem>

        <MenuItem value="sep3" isSeparator />

        <MenuItem
          value="logout"
          startIcon={<ExitToAppIcon />}
          onSelected={() => {
            logout.mutate();
          }}
        >
          <Trans message="Log out" />
        </MenuItem>
      </Menu>
    </MenuTrigger>
  );
}
